import { SharedSpinner } from '@components';
import { useAvailableWorkStations } from '@hooks';
import { L } from '@lib';
import { useStore } from '@stores';
import { Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

interface IProps {
  orderId?: string;
  startAt?: string | Date;
  endAt?: string | Date;
  excludeId?: string;
  mode?: 'multiple' | 'tags';
  value?: string | undefined;
  defaultValue?: { label: string; value: string }[] | undefined;
  allowClear?: boolean;
  className?: string;
  style?: React.CSSProperties | undefined;
  disabledValues?: string[];
  disabled?: boolean;
  placeholder?: string;
  onChange?:
    | ((value: string, option: DefaultOptionType | DefaultOptionType[] | undefined) => void)
    | undefined;
}

export const AvailableWorkStationSelect: React.FC<IProps> = observer(
  ({
    orderId,
    startAt,
    endAt,
    excludeId,
    mode,
    value,
    defaultValue,
    disabled = false,
    placeholder,
    className,
    disabledValues,
    style = { flexBasis: '100%' },
    onChange,
  }) => {
    const { tenantProfileStore } = useStore();
    const { workStationInUse, availableWorkStationsLoading } = tenantProfileStore;
    const availableWorkStations = useAvailableWorkStations({ orderId, startAt, endAt });

    const options = useMemo(() => {
      const defaultOptions =
        defaultValue?.map((item) => ({
          label: item.label,
          value: item.value,
        })) || [];

      const filteredOptions = workStationInUse
        ? availableWorkStations.filter((x) => x.id !== workStationInUse.id)
        : availableWorkStations;

      const furtherFilteredOptions = excludeId
        ? filteredOptions.filter((x) => x.id !== excludeId)
        : filteredOptions;

      const availableOptions = furtherFilteredOptions.map((workStation) => ({
        label: workStation.name,
        value: workStation.id,
      }));

      // Combine the options and filter duplicates
      const uniqueOptionsMap = new Map();
      [...defaultOptions, ...availableOptions].forEach((option) => {
        uniqueOptionsMap.set(`${option.label}-${option.value}`, option);
      });

      let allOptions = Array.from(uniqueOptionsMap.values());

      // Add disabled property if value is in disabledValues, and update label if disabled
      allOptions = allOptions.map((option) => {
        const isDisabled = disabledValues?.includes(option.value);
        return {
          ...option,
          disabled: isDisabled,
          label: isDisabled ? `${option.label} - unavailable` : option.label,
        };
      });

      allOptions.sort((a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0));

      // Move selected values to the top
      if (value) {
        const selectedValues = Array.isArray(value) ? value : [value];
        const selectedOptions = allOptions.filter((option) =>
          selectedValues.includes(option.value)
        );
        const otherOptions = allOptions.filter((option) => !selectedValues.includes(option.value));
        return [...selectedOptions, ...otherOptions];
      }

      return allOptions;
    }, [availableWorkStations, defaultValue, value, disabledValues, excludeId]);

    return (
      <Select
        mode={mode}
        className={className}
        style={style}
        disabled={disabled}
        onChange={onChange}
        placeholder={placeholder ?? L('Select workstation')}
        value={value}
        options={options}
        loading={availableWorkStationsLoading}
        dropdownRender={(menu) => (
          <div>
            {menu}
            {availableWorkStationsLoading && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <SharedSpinner loading={availableWorkStationsLoading} />
              </div>
            )}
          </div>
        )}
      />
    );
  }
);
